/**
 * WealthPlanCalculator — long-term financial-position + cash-flow projection.
 *
 * NZ tax assumptions baked in:
 *   - No capital gains tax on the projected asset (assumes long-hold past
 *     any brightline window). Toggle to model exit tax if the user wants.
 *   - Interest deductibility on rental property is OFF by default (matches
 *     the "not present at the moment" note the user gave). Toggle to switch
 *     back on for scenarios in the phased-in future.
 *   - Marginal tax on rental profit is applied at the user's chosen rate.
 *
 * Opportunity cost = the same up-front capital compounded in a "safe
 * alternative" (default 5% p.a., like a term deposit) over the same period.
 *
 * All maths client-side — no backend endpoint needed. Numbers refresh live
 * as the sliders / inputs change.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

function project(inputs) {
    const {
        years, upfront, annualIncome, annualExpenses, taxRatePct,
        assetType, purchasePrice, mortgageRate, rentalIncome, propertyExpenses,
        capitalGrowthPct, inflationPct, altReturnPct, interestDeductible, applyExitCgt, cgtRatePct,
    } = inputs;

    const rows = [];
    // Property setup
    const deposit = assetType === "property" ? Math.min(upfront, purchasePrice) : upfront;
    let mortgage = assetType === "property" ? Math.max(0, purchasePrice - deposit) : 0;
    let assetValue = assetType === "property" ? purchasePrice : upfront;
    let altValue = upfront;                                  // opportunity-cost bucket
    let cumulativeAfterTaxCash = 0;

    for (let y = 1; y <= years; y++) {
        // 1. Capital growth on the asset (annual compound).
        assetValue = assetValue * (1 + capitalGrowthPct / 100);

        // 2. Rental / income from the asset itself.
        const grossFromAsset = assetType === "property" ? rentalIncome : 0;
        const propOps = assetType === "property" ? propertyExpenses : 0;

        // 3. Mortgage interest cost + a modest 20-year straight-line principal repayment.
        const interest = mortgage * (mortgageRate / 100);
        const principal = assetType === "property" ? Math.min(mortgage, purchasePrice / 20) : 0;
        mortgage = Math.max(0, mortgage - principal);

        // 4. Rental taxable profit (NZ: interest currently NOT deductible by
        //    default — user can toggle on to model the phased-in future).
        const deductibleInterest = interestDeductible ? interest : 0;
        const rentalTaxableProfit = grossFromAsset - propOps - deductibleInterest;
        const rentalTax = Math.max(0, rentalTaxableProfit) * (taxRatePct / 100);

        // 5. Cash flow for the year from OWNING the asset (before personal
        //    living costs — which we surface separately).
        const assetCashFlow = grossFromAsset - propOps - interest - principal - rentalTax;
        cumulativeAfterTaxCash += assetCashFlow;

        // 6. Opportunity-cost pot: the same up-front $ compounded in a safe
        //    alternative + the shortfall the owner would have needed to top
        //    up if the asset was cash-flow negative.
        altValue = altValue * (1 + altReturnPct / 100) + Math.max(0, -assetCashFlow);

        // 7. Household affordability signal: personal surplus after living
        //    costs LESS any top-up they needed to feed the asset.
        const personalSurplus = annualIncome - annualExpenses - Math.max(0, -assetCashFlow);

        rows.push({
            year: y,
            assetValue,
            mortgage,
            equity: assetValue - mortgage,
            assetCashFlow,
            cumulativeAfterTaxCash,
            altValue,
            personalSurplus,
        });
    }

    const finalRow = rows[rows.length - 1];
    const grossGain = finalRow ? (finalRow.assetValue - (assetType === "property" ? purchasePrice : upfront)) : 0;
    const exitTax = applyExitCgt ? Math.max(0, grossGain) * (cgtRatePct / 100) : 0;
    const netEquityAtExit = (finalRow?.equity || 0) - exitTax + (finalRow?.cumulativeAfterTaxCash || 0);
    const netAlt = finalRow?.altValue || 0;
    const surplusVsAlt = netEquityAtExit - netAlt;
    // Real (inflation-adjusted) equivalent of the winning pot.
    const realWinner = Math.max(netEquityAtExit, netAlt) / Math.pow(1 + inflationPct / 100, Math.max(1, years));

    return { rows, finalRow, grossGain, exitTax, netEquityAtExit, netAlt, surplusVsAlt, realWinner };
}

const DEFAULTS = {
    years: 10,
    upfront: 100000,
    annualIncome: 120000,
    annualExpenses: 60000,
    taxRatePct: 33,
    assetType: "property",
    purchasePrice: 700000,
    mortgageRate: 6.5,
    rentalIncome: 32000,
    propertyExpenses: 6000,
    capitalGrowthPct: 4,
    inflationPct: 3,
    altReturnPct: 5,
    interestDeductible: false,
    applyExitCgt: false,
    cgtRatePct: 33,
};

export default function WealthPlanCalculator({ mode = "portal", initial = {}, onSignupNudge }) {
    const [inputs, setInputs] = useState({ ...DEFAULTS, ...initial });
    const s = (k, v) => setInputs((f) => ({ ...f, [k]: v }));
    const projection = useMemo(() => project(inputs), [inputs]);
    const winning = projection.netEquityAtExit >= projection.netAlt;

    return (
        <div className="space-y-5" data-testid="wealth-plan-calculator">
            {/* HERO — public mode shows a bigger marketing header */}
            {mode === "public" && (
                <div className="rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-800 text-white p-6 sm:p-8" data-testid="wealth-plan-hero">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium">
                        <Rocket className="h-3.5 w-3.5" /> Grow my wealth
                    </div>
                    <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Where will you be in {inputs.years} years?</h1>
                    <p className="mt-2 text-[14px] text-white/85 leading-relaxed max-w-2xl">
                        A no-nonsense NZ-flavoured wealth projection. Toggle a property or a diversified share pot, see the cash flow, opportunity cost, and inflation-adjusted final position. Free, no signup, no data leaves your browser.
                    </p>
                </div>
            )}

            {/* CONTROLS */}
            <div className="rounded-2xl border border-[#E4E4E7] bg-white p-5 sm:p-6 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <NumField label="Years" v={inputs.years} min={1} max={40} onChange={(v) => s("years", v)} testId="wp-years" />
                    <SelectField label="Asset type" v={inputs.assetType} onChange={(v) => s("assetType", v)} testId="wp-asset-type" options={[["property", "Rental property"], ["shares", "Shares / ETF"]]} />
                    <NumField label="Up-front capital ($)" v={inputs.upfront} step={5000} min={0} onChange={(v) => s("upfront", v)} testId="wp-upfront" />
                    {inputs.assetType === "property" && <>
                        <NumField label="Purchase price ($)" v={inputs.purchasePrice} step={10000} onChange={(v) => s("purchasePrice", v)} testId="wp-purchase" />
                        <NumField label="Mortgage rate (%)" v={inputs.mortgageRate} step={0.1} onChange={(v) => s("mortgageRate", v)} testId="wp-mortgage-rate" />
                        <NumField label="Annual rent ($)" v={inputs.rentalIncome} step={500} onChange={(v) => s("rentalIncome", v)} testId="wp-rent" />
                        <NumField label="Property costs ($/yr)" v={inputs.propertyExpenses} step={500} onChange={(v) => s("propertyExpenses", v)} testId="wp-prop-exp" />
                    </>}
                    <NumField label="Your annual income ($)" v={inputs.annualIncome} step={1000} onChange={(v) => s("annualIncome", v)} testId="wp-income" />
                    <NumField label="Annual living costs ($)" v={inputs.annualExpenses} step={1000} onChange={(v) => s("annualExpenses", v)} testId="wp-expenses" />
                    <NumField label="Marginal tax rate (%)" v={inputs.taxRatePct} step={1} onChange={(v) => s("taxRatePct", v)} testId="wp-tax" />
                    <NumField label="Capital growth (%/yr)" v={inputs.capitalGrowthPct} step={0.5} onChange={(v) => s("capitalGrowthPct", v)} testId="wp-growth" />
                    <NumField label="Inflation (%/yr)" v={inputs.inflationPct} step={0.1} onChange={(v) => s("inflationPct", v)} testId="wp-inflation" />
                    <NumField label="Safe alternative return (%/yr)" v={inputs.altReturnPct} step={0.1} onChange={(v) => s("altReturnPct", v)} testId="wp-alt" />
                </div>

                <div className="flex flex-wrap gap-3 pt-2 border-t border-[#F0EDE5]">
                    <Toggle label="Interest deductible on rental" checked={inputs.interestDeductible} onChange={(v) => s("interestDeductible", v)} testId="wp-interest-deductible" hint="Currently disabled by default — matches the NZ position at time of writing." />
                    <Toggle label="Model an exit tax (hypothetical CGT)" checked={inputs.applyExitCgt} onChange={(v) => s("applyExitCgt", v)} testId="wp-apply-cgt" hint="NZ has no CGT today. Turn on to stress-test 'what if CGT was introduced'." />
                    {inputs.applyExitCgt && (
                        <NumField label="Assumed CGT rate (%)" v={inputs.cgtRatePct} step={1} onChange={(v) => s("cgtRatePct", v)} testId="wp-cgt-rate" />
                    )}
                </div>
            </div>

            {/* HEADLINE ANSWER */}
            <div className={`rounded-2xl border p-5 sm:p-6 ${winning ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="wealth-plan-answer">
                <div className={`co-label ${winning ? "text-emerald-900" : "text-amber-900"}`}>Where you land after {inputs.years} years</div>
                <div className="mt-1 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F1115] tabular-nums" data-testid="wealth-plan-final">
                    {NZD.format(projection.netEquityAtExit)}
                </div>
                <p className={`mt-2 text-[13px] leading-relaxed ${winning ? "text-emerald-900" : "text-amber-900"}`} data-testid="wealth-plan-verdict">
                    {winning
                        ? `That's ${NZD.format(projection.surplusVsAlt)} more than the same money in a safe alternative at ${inputs.altReturnPct}% p.a.`
                        : `That's ${NZD.format(Math.abs(projection.surplusVsAlt))} LESS than the same money in a safe alternative at ${inputs.altReturnPct}% p.a. — this scenario doesn't beat the boring choice.`}
                </p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px] tabular-nums">
                    <Kpi label="Asset value" value={NZD.format(projection.finalRow?.assetValue || 0)} />
                    <Kpi label="Mortgage left" value={NZD.format(projection.finalRow?.mortgage || 0)} />
                    <Kpi label="Cash from asset" value={NZD.format(projection.finalRow?.cumulativeAfterTaxCash || 0)} />
                    <Kpi label="Safe alt pot" value={NZD.format(projection.netAlt)} />
                </div>
                <p className="mt-3 text-[12px] text-[#5E636E]">
                    Inflation-adjusted winning pot in today&apos;s dollars: <span className="font-medium text-[#0F1115]">{NZD.format(projection.realWinner)}</span>. Numbers are estimates — talk to your accountant before you buy.
                </p>
            </div>

            {/* YEAR-BY-YEAR TABLE */}
            <div className="rounded-2xl border border-[#E4E4E7] bg-white p-4 sm:p-5 overflow-x-auto" data-testid="wealth-plan-table">
                <table className="w-full text-[13px] tabular-nums">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            <th className="py-2 pr-3">Year</th>
                            <th className="py-2 pr-3">Asset value</th>
                            <th className="py-2 pr-3">Equity</th>
                            <th className="py-2 pr-3">Cash / year</th>
                            <th className="py-2 pr-3">Safe alt pot</th>
                        </tr>
                    </thead>
                    <tbody>
                        {projection.rows.map((r) => (
                            <tr key={r.year} className="border-t border-[#F0EDE5]">
                                <td className="py-1.5 pr-3">{r.year}</td>
                                <td className="py-1.5 pr-3">{NZD.format(r.assetValue)}</td>
                                <td className="py-1.5 pr-3">{NZD.format(r.equity)}</td>
                                <td className={`py-1.5 pr-3 ${r.assetCashFlow < 0 ? "text-rose-700" : ""}`}>{NZD.format(r.assetCashFlow)}</td>
                                <td className="py-1.5 pr-3">{NZD.format(r.altValue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Assumptions panel */}
            <div className="rounded-xl bg-white border border-[#E4E4E7] p-4 text-[12px] text-[#5E636E] flex items-start gap-2" data-testid="wealth-plan-assumptions">
                <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                    NZ assumptions baked in: no capital gains tax on long-hold; interest deductibility on rentals defaults to OFF (toggle above). Straight-line 20-year principal repayment. Opportunity cost is the same up-front $ compounded at your chosen &quot;safe&quot; return, plus any yearly top-up you&apos;d need if the asset ran cash-flow negative. Not financial advice.
                </div>
            </div>

            {/* Public-mode signup nudge */}
            {mode === "public" && (
                <div className="rounded-2xl bg-[#0F1115] text-white p-5 sm:p-6" data-testid="wealth-plan-signup-nudge">
                    <div className="co-label text-white/60">Keep this plan</div>
                    <h3 className="mt-1 font-display text-xl font-semibold tracking-tight">Sign up free — save this plan &amp; watch it update as your real numbers change.</h3>
                    <p className="mt-2 text-[13px] text-white/70 max-w-xl leading-relaxed">
                        ClientOps auto-fills your income and expenses from Xero or Prosaic and tracks the plan month by month. Free forever for the basics — no card needed.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => onSignupNudge?.(inputs)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 text-emerald-950 font-medium px-4 py-2 text-sm hover:bg-emerald-300"
                            data-testid="wealth-plan-signup-btn"
                        >
                            Sign up free <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                        <Link to="/" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/10" data-testid="wealth-plan-learn-more">
                            About ClientOps
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------ tiny inputs */
function NumField({ label, v, onChange, step = 1, min = 0, max, testId }) {
    return (
        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
            {label}
            <input
                type="number"
                inputMode="decimal"
                value={v}
                step={step}
                min={min}
                max={max}
                onChange={(e) => onChange(Number(e.target.value))}
                className="mt-1 rounded-md border border-[#E4E4E7] bg-white px-3 py-1.5 text-sm text-[#0F1115] normal-case tracking-normal"
                data-testid={testId}
            />
        </label>
    );
}
function SelectField({ label, v, onChange, options, testId }) {
    return (
        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
            {label}
            <select
                value={v}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 rounded-md border border-[#E4E4E7] bg-white px-3 py-1.5 text-sm text-[#0F1115] normal-case tracking-normal"
                data-testid={testId}
            >
                {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
            </select>
        </label>
    );
}
function Toggle({ label, checked, onChange, testId, hint }) {
    return (
        <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <span className="inline-flex items-center gap-2 text-sm text-[#0F1115]">
                <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
                {label}
            </span>
            {hint && <span className="text-[11px] text-[#5E636E]">{hint}</span>}
        </label>
    );
}
function Kpi({ label, value }) {
    return (
        <div className="rounded-md bg-white/60 px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">{label}</div>
            <div className="text-[13px] font-medium text-[#0F1115]">{value}</div>
        </div>
    );
}
